import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@emotion/react';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';

import Alert, { useAlert } from 'components/snacks/Alert';

import CollapseUserService from 'components/collapse/UserServices';

import Data from 'utils/Data';
import { serviceColumns } from 'utils/DataBase';
import { getServiceList } from 'api/Api';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };
  useEffect(() => {
    const selector1 = document.querySelector('#servicePagination p');
    if (selector1) {
      selector1.classList.add('yekan-font');
      selector1.innerHTML = 'ردیف در هر صفحه';
    }
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        sx={{ mr: 2.5 }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

export default function StickyHeadTable({
  serviceList = [],
  serviceLength = 0,
  sendApi = true
}) {
  const useStyles = makeStyles((theme) => ({
    headerContainer: {
      position: 'relative',
      backgroundColor: theme.palette.secondary.main + '!important',
      border: 0,
      borderRadius: 0,
      boxShadow: 'rgb(0 0 0 / 15%) 0px 1px 10px',
      color: 'white',
      height: 85,
      padding: '0 55px',
      [theme.breakpoints.down('md')]: {
        padding: '0 15px',
        height: 65
      },
      transition: 'all .3s ease'
    }
  }));

  const [data, setData] = useState([]);
  const [len, setLen] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDropdown, setShowDropdown] = useState(false);

  const [search, setSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(data);
  useEffect(() => {
    setFilteredUsers(data);
  }, [data]);

  const handleInputChange = (e) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);

    if (!searchTerm) {
      setFilteredUsers(data);
    } else {
      const filteredItems = data.filter(
        (item) =>
          String(item.product_name)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          String(item.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(item.user_label)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          String(item.ip).toLowerCase().includes(searchTerm.toLowerCase())
      );

      setFilteredUsers(filteredItems);
    }
  };

  const alert_timeout = 10000;
  const alertStore = useAlert(alert_timeout);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const [currentPath, setCurrentPath] = useState('');
  useEffect(() => {
    const path = window.location.pathname;
    setCurrentPath(path);
    if (sendApi) {
      setLoading(true);
      getServiceList((isOk, data) => {
        if (isOk) {
          setData(data.services);
          setLen(data.services.length);
          setLoading(false);
        } else {
          setError(true);
          alertStore.AddAlert({
            key: '400',
            message: data,
            severity: 'error',
            auto_hide: true
          });
        }
      });
    } else {
      setError(false);
      setLoading(false);
      setLen(serviceLength);
      setData(serviceList);
    }
  }, []);

  const handleStatusColor = (status) => {
    switch (parseInt(status)) {
      case 1: {
        return 'badge badge-light-success';
      }
      case 2: {
        return 'badge badge-light-danger';
      }
      case 3: {
        return 'badge badge-light-warning';
      }
      default: {
        return 'badge badge-light-warning';
      }
    }
  };

  return (
    <>
      {loading ? (
        <div
          className={'d-flex justify-content-center align-items-center'}
          style={{
            position: 'fixed',
            width: '100%',
            height: '100%',
            left: '0',
            top: '0'
          }}
        >
          <Box sx={{ display: 'flex', marginLeft: '20px' }}>
            <CircularProgress />
          </Box>
          <h1>در حال بارگذاری...</h1>
        </div>
      ) : (
        <>
          {!error && (
            // padding: '20px 35px'
            <Paper
              id={'serviceCard'}
              elevation={1}
              sx={{ overflow: 'hidden' }}
              className={`normal-shadow rounded-lg py-2 px-2 px-lg-10 py-lg-4 ${
                currentPath === '/services' ? 'mh-75' : ''
              }`}
            >
              {currentPath === '/' && (
                <div className="card-header d-flex justify-content-between align-items-center border-0 pt-5">
                  <h3 className="card-title d-flex align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">
                      سرویس های من
                    </span>
                  </h3>
                  <div
                    className="card-toolbar"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    data-bs-trigger="hover"
                    title=""
                    data-bs-original-title="مشاهده سرویس ها"
                  >
                    <Link
                      to={'/services'}
                      className="btn btn-sm btn-primary btn-light btn-active-primary"
                    >
                      <span className={'f-09'}>مشاهده همه</span>
                    </Link>
                  </div>
                </div>
              )}
              {currentPath === '/services' && (
                <div
                  className="card-header d-flex justify-content-between align-items-center py-3 gap-2 gap-md-5"
                  data-select2-id="select2-data-133-8g4r"
                >
                  <div className="card-title d-flex align-items-center justify-content-between position-relative my-1 w-100">
                    <div className="d-flex align-items-center gap-5 services-list">
                      <h6 className='text-nowrap'>لیست سرویس های من</h6>
                      <input
                        type="search"
                        placeholder="جستجو..."
                        className="search-inp border-none"
                        value={search}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={'position-relative services-list-button'}>
                      <button
                        className="btn btn-success text-nowrap"
                        onClick={() => setShowDropdown(!showDropdown)}
                      >
                        خرید سرویس
                      </button>
                      {showDropdown && (
                        <>
                          <div
                            className="dropdown-collapse"
                            onClick={() => setShowDropdown(false)}
                          />
                          <div className="dropdown-custom">
                            {Data.sidebar.servers_category.map(
                              (category, index) => (
                                <div
                                  className="dropdown-custom-item"
                                  key={index}
                                >
                                  <Link className="menu-link" to={category.url}>
                                    <span className="menu-bullet me-2">
                                      <span className="bullet bullet-dot" />
                                    </span>
                                    <span className="menu-title">
                                      {category.name}
                                    </span>
                                  </Link>
                                </div>
                              )
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {data.length ? (
                <>
                  <TableContainer className="d-none d-md-block">
                    <Table stickyHeader aria-label="sticky table">
                      <TableHead sx={{ BackgroundColor: '#eaeaea' }}>
                        <TableRow>
                          {serviceColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              style={{
                                minWidth: column.minWidth,
                                fontFamily: 'yekan',
                                fontSize: '1em',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUsers &&
                          filteredUsers
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((row, index) => {
                              return (
                                <TableRow
                                  hover
                                  role="checkbox"
                                  tabIndex={-1}
                                  key={index}
                                  style={{
                                    backgroundColor:
                                      index % 2 === 0 ? '#f3f3f3' : ''
                                  }}
                                >
                                  {serviceColumns.map((column) => {
                                    const value = row[column.id];
                                    return (
                                      <TableCell
                                        key={column.id}
                                        align={column.align}
                                        sx={{
                                          fontFamily: 'yekan' + '!important'
                                        }}
                                      >
                                        {column.id === 'ID' && (
                                          <span>{page * rowsPerPage + index + 1}</span>
                                        )}
                                        {column.id === 'id' && (
                                          <span>{'#' + row['id']}</span>
                                        )}

                                        {column.id === 'IP' && (
                                          <span>
                                            {row['ip'] ? row['ip'] : '-'}
                                          </span>
                                        )}
                                        {column.id === 'label' && (
                                          <span>
                                            {row['user_label']
                                              ? row['user_label']
                                              : '-'}
                                          </span>
                                        )}

                                        {column.id === 'detail' && (
                                          <Link
                                            to={{
                                              pathname:
                                                '/services/service-detail',
                                              state: {
                                                id: row['id'],
                                                name: row['product_name'],
                                                validity:
                                                  row['service_validity'],
                                                st_date: row['start_date'],
                                                ex_date: row['expire_date']
                                              }
                                            }}
                                            className={'text-primary'}
                                          >
                                            <span className={'me-2'}>
                                              مشاهده
                                            </span>
                                            <span className="svg-icon svg-icon-2 svg-icon-primary">
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{
                                                  transform: 'rotate(180deg)'
                                                }}
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                              >
                                                <rect
                                                  opacity="0.5"
                                                  x="18"
                                                  y="13"
                                                  width="13"
                                                  height="2"
                                                  rx="1"
                                                  transform="rotate(-180 18 13)"
                                                  fill="#009ef7"
                                                />
                                                <path
                                                  d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z"
                                                  fill="#009ef7"
                                                />
                                              </svg>
                                            </span>
                                          </Link>
                                        )}

                                        {column.id === 'status' && (
                                          <span
                                            className={handleStatusColor(
                                              row['status']
                                            )}
                                          >
                                            {row['status_str']}
                                          </span>
                                        )}
                                        {column.id !== 'detail' &&
                                          column.id !== 'ID' &&
                                          column.id !== 'id' &&
                                          column.id !== 'status' && (
                                            <span>{value}</span>
                                          )}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TableContainer className="d-none d-md-block">
                    <Table>
                      <TableHead />
                      <TableBody>
                        <TableRow>
                          <TablePagination
                            id={'servicePagination'}
                            rowsPerPageOptions={[
                              5,
                              10,
                              25,
                              {
                                label: 'All',
                                value: 1000
                              }
                            ]}
                            colSpan={4}
                            count={len}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                              inputProps: {
                                'aria-label': 'rows per page'
                              },
                              native: true
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            style={{ fontFamily: 'unset', display: 'flex' }}
                          />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* mobile items */}
                  {data &&
                    data
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        return (
                          <>
                            <div
                              className="d-md-none"
                              key={`user-service-${index}`}
                            >
                              <CollapseUserService
                                item={row}
                                columns={serviceColumns}
                              />
                            </div>
                          </>
                        );
                      })}
                </>
              ) : (
                <>
                  <div className="alert alert-warning text-center mt-3">
                    <h4 className="m-0">شما سرویسی ثبت نکرده اید</h4>
                  </div>
                </>
              )}
            </Paper>
          )}
        </>
      )}
      {
        <Alert
          alerts={alertStore.alert}
          close={alertStore.RemoveAlert}
          timeout={alert_timeout}
        />
      }
    </>
  );
}
